import axiosInstance from "../../interceptors/axiosInstance";
import { getSessionData } from "../../interceptors/localStorageService";
import { getApiBaseUrl } from "../../../shared/config/tenantConfiguration";
import { SelfUserClassificationEnum } from "../../../shared/constants/constants";
import { getSelectedBranchHeaders } from "../../branchSelection";

// Dynamically get baseUrl for each request to support tenant switching
const getBaseUrl = () => getApiBaseUrl();

const PUBLIC_ROUTES = [
  '/authentication/login',
  '/authentication/admin/login',
  '/authentication/register',
  '/authentication/forgot-password',
];

const isPublicRoute = () => {
  const currentPath = window.location.pathname;
  return PUBLIC_ROUTES.some(route => currentPath.includes(route));
};

// ─────────────────────────────────────────────────────────────
// 1. postRequest – basic unauthenticated POST
// ─────────────────────────────────────────────────────────────
export const postRequest = async (command, data, language, additionalHeader = null) => {
  let endpoint = "/api/bpm/cmd";
  if (language) {
    endpoint = endpoint.concat("?lang=").concat(language);
  }

  const dataToSend = {
    cmd: command,
    data: JSON.stringify(data),
  };

  try {
    const response = await axiosInstance.post(endpoint, dataToSend, {
      headers: {
        ...(additionalHeader || {}),
      },
    });
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

// ─────────────────────────────────────────────────────────────
// 2. postRequestSimulator – self‑service POST (unauthenticated)
// ─────────────────────────────────────────────────────────────
export const postRequestSimulator = async (command, data, additionalHeader = null) => {
  let endpoint = "/api/bpm/selfservice/cmd";

  const dataToSend = {
    cmd: command,
    data,
  };

  try {
    const response = await axiosInstance.post(endpoint, dataToSend, {
      headers: {
        ...(additionalHeader || {}),
      },
    });
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

// ─────────────────────────────────────────────────────────────
// 3. postAuthenticatedRequest – authenticated POST
// ─────────────────────────────────────────────────────────────
let _isHandlingAuthFailure = false;

export const postAuthenticatedRequest = async (
  command,
  data,
  isBackOffice = false,
  additionalHeader = null,
) => {
  let endpoint = isBackOffice
    ? "/api/core/backoffice/cmd"
    : "/api/bpm/selfservice/cmd";
  const branchHeaders = getSelectedBranchHeaders();

  const dataToSend = {
    cmd: command,
    data: JSON.stringify(data),
  };

  console.log("additionalHeader?? ",additionalHeader)

  try {
    const response = await axiosInstance.post(endpoint, dataToSend, {
      headers: {
        ...branchHeaders,
        ...(additionalHeader || {}),
      },
    });
    return response.data;
  } catch (error) {
    const statusCode = error?.response?.status;

    if (statusCode === 401) {
      // ✅ Check if already on public route
      if (isPublicRoute()) {
        console.log('[postAuthenticatedRequest] 401 on public route, skipping event dispatch');
        const authError = new Error('');
        authError.statusCode = 401;
        authError.originalError = error;
        return Promise.reject(authError);
      }

      // Only dispatch once for a burst of 401s
      if (!_isHandlingAuthFailure) {
        _isHandlingAuthFailure = true;
        const sessionData = getSessionData() || {};
        const isAdmin =
          sessionData?.userClassification ===
          SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value;

        let loginRoute = isAdmin ? "/authentication/admin/login" : "/authentication/login";

        console.log("Dispatching auth:expired event");
        window.dispatchEvent(new CustomEvent("auth:expired", {
          detail: { message: "Your session has expired. The application will log you out.", loginRoute }
        }));
        // Allow future expiry events after a grace period
        setTimeout(() => { _isHandlingAuthFailure = false; }, 8000);
      }

      const authError = new Error('');
      authError.statusCode = 401;
      authError.originalError = error;
      return Promise.reject(authError);
    }

    // For non-401, normalize to Error instance if needed
    if (!(error instanceof Error)) {
      const norm = new Error(error?.message || "Request failed");
      norm.originalError = error;
      return Promise.reject(norm);
    }
    return Promise.reject(error);
  }
};
